import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatTimeFromDate } from '../utils/dateFormatting';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { 
  ArrowLeft, 
  Edit, 
  Copy, 
  Send, 
  Trash2,
  Building2,
  MapPin,
  Calendar,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRightCircle,
  Eye,
  ExternalLink,
  Mail,
  Briefcase,
  Printer
} from 'lucide-react';
import { format, parseISO, addDays, parse } from 'date-fns';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-zinc-500 hover:bg-zinc-600', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500 hover:bg-blue-600', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-500 hover:bg-purple-600', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-500 hover:bg-green-600', icon: CheckCircle },
  accepted: { label: 'Accepted', color: 'bg-green-500 hover:bg-green-600', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500 hover:bg-red-700', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-orange-500 hover:bg-orange-600', icon: AlertCircle },
  converted: { label: 'Converted', color: 'bg-amber-500 text-black hover:bg-amber-600', icon: ArrowRightCircle }
};

const QuoteView = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeFormat, setTimeFormat] = useState('24h');
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [converting, setConverting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [convertData, setConvertData] = useState({
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    description: ''
  });

  const fetchQuote = async () => {
    try {
      const response = await api.get(`/quotes/${id}`);
      setQuote(response.data);
    } catch (error) {
      toast.error('Failed to load quote');
      navigate('/quotes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuote();
    if (user?.client_id) {
      api.get(`/tenants/${user.client_id}`).then(res => {
        if (res.data?.time_format) setTimeFormat(res.data.time_format);
      }).catch(() => {});
    }
  }, [api, id]);

  const handleDuplicate = async () => {
    try {
      const response = await api.post(`/quotes/${id}/duplicate`);
      toast.success('Quote duplicated as new draft');
      navigate(`/quotes/${response.data.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate quote');
    }
  };

  const handleSend = async () => {
    try {
      await api.post(`/quotes/${id}/send`);
      toast.success('Quote sent');
      fetchQuote();
    } catch (error) {
      toast.error('Failed to send quote');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this quote?')) return;
    try {
      await api.delete(`/quotes/${id}`);
      toast.success('Quote deleted');
      navigate('/quotes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quote');
    }
  };

  const handlePrint = async () => {
    setGeneratingPdf(true);
    try {
      const response = await api.get(`/quotes/${id}/pdf`, {
        responseType: 'blob'
      });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      // Clean up the object URL after a delay
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 30000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleConvertToJob = async () => {
    if (!convertData.start_date) {
      toast.error('Please select a start date');
      return;
    }
    
    setConverting(true);
    try {
      const response = await api.post(`/quotes/${id}/convert-to-job`, convertData);
      toast.success(response.data.message);
      setShowConvertDialog(false);
      navigate(`/schedule?job=${response.data.job_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert quote to job');
    } finally {
      setConverting(false);
    }
  };

  const formatCurrency = (value) => {
    const symbol = quote?.currency_symbol || '$';
    return `${symbol}${new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0)}`;
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1 text-sm px-3 py-1`}>
        <Icon className="h-4 w-4" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading quote...</div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Quote not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quote-view-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/quotes')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl md:text-3xl font-bold font-mono">
                {quote.quote_number}
              </h1>
              <StatusBadge status={quote.status} />
            </div>
            <p className="text-muted-foreground">
              Created {format(parseISO(quote.created_at), 'dd MMM yyyy')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {quote.status === 'draft' && (
            <Button 
              variant="outline" 
              onClick={() => navigate(`/quotes/${id}/edit`)}
              data-testid="edit-quote-btn"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {(quote.status === 'draft' || quote.status === 'sent') && (
            <Button onClick={handleSend} className="btn-volt" data-testid="send-quote-btn">
              <Send className="h-4 w-4 mr-2" />
              {quote.status === 'draft' ? 'Send' : 'Resend'}
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={handlePrint}
            disabled={generatingPdf}
            data-testid="print-quote-btn"
          >
            <Printer className="h-4 w-4 mr-2" />
            {generatingPdf ? 'Generating...' : 'Print'}
          </Button>
          <Button variant="outline" onClick={handleDuplicate}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate
          </Button>
          {quote.status === 'draft' && (
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Company</p>
                  <p className="font-medium">{quote.customer_name}</p>
                </div>
                {quote.contact_name && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Contact</p>
                    <p className="font-medium" data-testid="quote-view-contact-name">{quote.contact_name}</p>
                  </div>
                )}
                {(quote.contact_email || quote.customer_email) && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Email</p>
                    <p className="font-medium flex items-center gap-2" data-testid="quote-view-contact-email">
                      <Mail className="h-4 w-4" />
                      {quote.contact_email || quote.customer_email}
                    </p>
                  </div>
                )}
                {quote.site_name && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Site</p>
                    <p className="font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {quote.site_name}
                    </p>
                  </div>
                )}
                {quote.site_address && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Address</p>
                    <p className="font-medium">{quote.site_address}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Line Items</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wider">Description</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Qty</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Unit</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Rate</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quote.line_items?.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.description}</p>
                          <p className="text-xs text-muted-foreground capitalize">{item.item_type}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">{item.quantity}</TableCell>
                      <TableCell className="capitalize">{item.unit}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(item.rate)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {formatCurrency(item.quantity * item.rate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          {quote.terms_and_conditions && (
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Terms & Conditions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{quote.terms_and_conditions}</p>
              </CardContent>
            </Card>
          )}

          {/* Internal Notes */}
          {quote.notes && (
            <Card className="card-industrial border-orange-500/30">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-orange-500">Internal Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{quote.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Totals */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {/* Show fuel levy summary row only for old quotes without an explicit line item */}
                {quote.fuel_levy_amount > 0 && !(quote.line_items || []).some(li => li.item_type === 'fuel_levy') && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Fuel Levy ({quote.fuel_levy_rate}%)</span>
                    <span className="font-mono">{formatCurrency(quote.fuel_levy_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">{formatCurrency(quote.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{quote.tax_name || 'GST'} ({quote.tax_rate}%)</span>
                  <span className="font-mono">{formatCurrency(quote.tax_amount)}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-mono font-bold text-2xl dark:text-[#ffd108]">
                    {formatCurrency(quote.total)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quote Details */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Prepared By</p>
                <p className="font-medium" data-testid="quote-view-prepared-by">{quote.created_by_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Pricing Type</p>
                <p className="font-medium capitalize">{quote.pricing_type}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Valid Until</p>
                <p className="font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {quote.valid_until ? format(parseISO(quote.valid_until), 'dd MMM yyyy') : '-'}
                </p>
              </div>
              {quote.sent_at && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Sent At</p>
                  <p className="font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {format(parseISO(quote.sent_at), 'dd MMM yyyy')} {formatTimeFromDate(parseISO(quote.sent_at), timeFormat)}
                  </p>
                </div>
              )}
              {quote.sent_to_email && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Sent To</p>
                  <p className="font-medium">{quote.sent_to_email}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Approval Info */}
          {(quote.status === 'approved' || quote.status === 'rejected') && (
            <Card className={`card-industrial ${quote.status === 'approved' ? 'border-green-500/30' : 'border-red-500/30'}`}>
              <CardHeader>
                <CardTitle className={`font-heading text-lg ${quote.status === 'approved' ? 'text-green-500' : 'text-red-500'}`}>
                  {quote.status === 'approved' ? 'Approval Details' : 'Rejection Details'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {quote.customer_signed_name && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Signed By</p>
                    <p className="font-medium">{quote.customer_signed_name}</p>
                  </div>
                )}
                {quote.customer_signed_at && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Signed At</p>
                    <p className="font-medium">
                      {format(parseISO(quote.customer_signed_at), 'dd MMM yyyy')} {formatTimeFromDate(parseISO(quote.customer_signed_at), timeFormat)}
                    </p>
                  </div>
                )}
                {quote.customer_rejection_reason && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Reason</p>
                    <p className="font-medium">{quote.customer_rejection_reason}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Convert to Job (for approved quotes) */}
          {quote.status === 'approved' && !quote.converted_job_id && (
            <Card className="card-industrial border-[#ffd108]/30">
              <CardContent className="pt-6">
                <Button 
                  className="w-full btn-volt" 
                  onClick={() => setShowConvertDialog(true)}
                  data-testid="convert-to-job-btn"
                >
                  <Briefcase className="h-4 w-4 mr-2" />
                  Convert to Job
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Create a scheduled job from this quote
                </p>
              </CardContent>
            </Card>
          )}

          {/* Linked Job */}
          {quote.converted_job_id && (
            <Card className="card-industrial border-[#ffd108]/30">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Linked Job</CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(`/schedule?job=${quote.converted_job_id}`)}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Job
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Convert to Job Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-[#ffd108]" />
              Convert Quote to Job
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Create a new job from quote <span className="font-mono text-[#ffd108]">{quote?.quote_number}</span> 
              for customer <span className="font-medium">{quote?.customer_name}</span>.
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date <span className="text-red-500">*</span></Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-mono"
                        data-testid="convert-start-date"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {convertData.start_date 
                          ? format(parse(convertData.start_date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
                          : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={convertData.start_date ? parse(convertData.start_date, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            setConvertData(prev => ({ ...prev, start_date: format(date, 'yyyy-MM-dd') }));
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-mono"
                        data-testid="convert-end-date"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {convertData.end_date 
                          ? format(parse(convertData.end_date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
                          : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={convertData.end_date ? parse(convertData.end_date, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            setConvertData(prev => ({ ...prev, end_date: format(date, 'yyyy-MM-dd') }));
                          }
                        }}
                        disabled={(date) => convertData.start_date && date < parse(convertData.start_date, 'yyyy-MM-dd', new Date())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Job Description (Optional)</Label>
                <Input
                  value={convertData.description}
                  onChange={(e) => setConvertData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Leave blank to auto-generate from quote items"
                  data-testid="convert-description"
                />
              </div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">
                The job will include the quote pricing ({formatCurrency(quote?.total)}) 
                and will be linked back to this quote.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)} disabled={converting}>
              Cancel
            </Button>
            <Button 
              className="btn-volt" 
              onClick={handleConvertToJob} 
              disabled={converting}
              data-testid="confirm-convert-btn"
            >
              {converting ? 'Converting...' : 'Create Job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteView;
